/**
 * Spotify Web Playback SDK wrapper: one in-browser device, full tracks,
 * Premium accounts only. connect() resolves false for free accounts (the SDK
 * fires account_error) — callers then stay on the Apple/preview path.
 *
 * Playback only: streams are never captured or analyzed (DRM).
 */
import {getSpotifyToken} from './SpotifyAuth';

let player = null;
let deviceId = null;
let premiumOk = true;
let sdkPromise = null;
let connectPromise = null;
let prevState = null;
const stateListeners = new Set();
const resolveCache = new Map();

/** Subscribe to {state, ended} events. Returns unsubscribe. */
export function onSpotifyState(cb) {
    stateListeners.add(cb);
    return () => stateListeners.delete(cb);
}

function loadSdk() {
    if (sdkPromise) {
        return sdkPromise;
    }
    sdkPromise = new Promise((resolve) => {
        window.onSpotifyWebPlaybackSDKReady = () => resolve();
        const s = document.createElement('script');
        s.src = 'https://sdk.scdn.co/spotify-player.js';
        document.head.appendChild(s);
    });
    return sdkPromise;
}

/**
 * Create + connect the device. Resolves true only when the device is ready
 * AND the account can stream (Premium). Safe to call repeatedly.
 */
export function connectSpotify() {
    if (connectPromise) {
        return connectPromise;
    }
    connectPromise = (async () => {
        const token = await getSpotifyToken();
        if (!token) {
            return false;
        }
        await loadSdk();
        player = new window.Spotify.Player({
            name: 'AbTunes Player',
            getOAuthToken: (cb) => getSpotifyToken().then((t) => t && cb(t)),
            volume: 1,
        });
        const ready = new Promise((resolve) => {
            const timer = setTimeout(() => resolve(false), 12000);
            player.addListener('ready', ({device_id}) => {
                deviceId = device_id;
                clearTimeout(timer);
                resolve(true);
            });
            player.addListener('account_error', () => {
                premiumOk = false; // free account — SDK can't stream
                clearTimeout(timer);
                resolve(false);
            });
            player.addListener('initialization_error', () => {
                clearTimeout(timer);
                resolve(false);
            });
            player.addListener('authentication_error', () => {
                clearTimeout(timer);
                resolve(false);
            });
        });
        player.addListener('not_ready', () => {
            deviceId = null;
        });
        player.addListener('player_state_changed', (state) => {
            if (!state) {
                return;
            }
            // End-of-track heuristic (the SDK has no explicit event): playback
            // snapped back to paused-at-0 while the finished track sits in
            // previous_tracks.
            const ended = state.paused && state.position === 0
                && prevState && !prevState.paused && prevState.position > 0
                && state.track_window?.previous_tracks?.some(
                    (t) => t.id === prevState.track_window?.current_track?.id);
            prevState = state;
            stateListeners.forEach((cb) => cb({state, ended: Boolean(ended)}));
        });
        const ok = await player.connect() && await ready;
        return ok && premiumOk && Boolean(deviceId);
    })();
    connectPromise.catch(() => {});
    return connectPromise;
}

/** "Artist - Title" -> best Spotify track (cached, null when no match). */
export async function resolveSpotifyTrack(artistTitle) {
    if (!artistTitle) {
        return null;
    }
    if (resolveCache.has(artistTitle)) {
        return resolveCache.get(artistTitle);
    }
    const token = await getSpotifyToken();
    if (!token) {
        return null;
    }
    const res = await fetch(
        `https://api.spotify.com/v1/search?type=track&limit=1&q=${encodeURIComponent(artistTitle)}`,
        {headers: {Authorization: `Bearer ${token}`}});
    if (!res.ok) {
        return null; // don't cache transient failures
    }
    const item = (await res.json())?.tracks?.items?.[0] || null;
    const track = item ? {
        uri: item.uri,
        name: item.name,
        artists: item.artists.map((a) => a.name).join(', '),
        image: item.album?.images?.[0]?.url || null,
        duration_ms: item.duration_ms,
    } : null;
    resolveCache.set(artistTitle, track);
    return track;
}

export async function playSpotifyTrack(uri) {
    const token = await getSpotifyToken();
    if (!token || !deviceId) {
        throw new Error('spotify device not ready');
    }
    const res = await fetch(
        `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
            method: 'PUT',
            headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
            body: JSON.stringify({uris: [uri]}),
        });
    if (!res.ok && res.status !== 204) {
        throw new Error(`spotify play: ${res.status}`);
    }
}

export const pauseSpotify = () => player?.pause().catch(() => {});
export const resumeSpotify = () => player?.resume().catch(() => {});
export const seekSpotifyMs = (ms) => player?.seek(Math.round(ms)).catch(() => {});
export const setSpotifyVolume = (v) => player?.setVolume(Math.min(1, Math.max(0, v))).catch(() => {});
export const getSpotifyState = () => (player ? player.getCurrentState() : Promise.resolve(null));

export function disconnectSpotifyPlayer() {
    if (player) {
        player.disconnect();
        player = null;
        deviceId = null;
        connectPromise = null;
        prevState = null;
    }
}
